import React from 'react'
import { Search, Filter } from 'lucide-react';
import PageHeader from '../../componentsII/PageHeader';
import api from '../../axios/api';
import { TableContainer, TableBody, TableCell, Switch, TableFooter, TableHead, TablePagination, TableRow, Table, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useEffect, useState } from 'react';
import { X, Trash2 as DeleteIcon, PenBoxIcon, PlusIcon } from 'lucide-react';
import CustomButton from '../../componentsII/Buttons';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../redux/slices/notificationSlice';
import { TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import debounce from 'lodash/debounce';
import { format } from "date-fns";
import AddUpdateElectricityEmissionModal from '../../components/Masters/EmissionElectricityMaster/AddUpdateElectricityEmissionModal';

const EmissionElectricityMaster = () => {
  const dispatch = useDispatch();

  const [isAddUpdateElectricityEmissionModalOpen, setIsAddUpdateElectricityEmissionModalOpen] = useState(false);
  const [selectedElectricityEmission, setSelectedElectricityEmission] = useState(null);

  const [electricityEmissions, setElectricityEmissions] = useState([]);
  const [totalElectricityEmissions, setTotalElectricityEmissions] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Enhanced filters state
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    sortBy: 'created_date',
    sortOrder: 'ASC',
    country: '',
    year: '',
    unit: '',
    consumptionType: ''
  });

  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    countries: [],
    years: [],
    units: [],
    consumptionTypes: []
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [electricityToDelete, setElectricityToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const handleEdit = (emission) => {
    setIsAddUpdateElectricityEmissionModalOpen(true);
    setSelectedElectricityEmission(emission);
  };

  // Get filter options separately from main data fetch
  const getFilterOptions = async () => {
    try {
      const response = await api.get('/admin/getElectricityEmissionFilterOptions');

      if (response.data && response.data.status === "200") {
        setFilterOptions({
          countries: response.data.countries || [],
          years: response.data.years || [],
          units: response.data.units || [],
          consumptionTypes: response.data.consumptionTypes || []
        });
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);

      // Fallback: Extract from current data if API call fails
      if (electricityEmissions.length > 0) {
        const uniqueCountries = [...new Set(electricityEmissions.map(item => item.country_name))];
        const uniqueYears = [...new Set(electricityEmissions.map(item => item.valid_year))];
        const uniqueUnits = [...new Set(electricityEmissions.map(item => item.unit_name))];
        const uniqueConsumptionTypes = [...new Set(electricityEmissions.map(item => item.consumption_type))];

        setFilterOptions({
          countries: uniqueCountries,
          years: uniqueYears,
          units: uniqueUnits,
          consumptionTypes: uniqueConsumptionTypes
        });
      }
    }
  };

  const getAllElectricityEmissions = async (pageNumber = page, limit = rowsPerPage, filterParams = filters) => {
    try {
      const response = await api.get('/admin/getElectricityEmission', {
        params: {
          page: pageNumber + 1,
          limit,
          search: filterParams.search,
          status: filterParams.status,
          sortBy: filterParams.sortBy,
          sortOrder: filterParams.sortOrder,
          country: filterParams.country,
          year: filterParams.year,
          unit: filterParams.unit,
          consumptionType: filterParams.consumptionType
        }
      });
      setElectricityEmissions(response.data.data);
      setTotalElectricityEmissions(response.data.total);
    } catch (error) {
      dispatch(showNotification({
        message: 'Failed to fetch Electricity Emission List',
        type: 'error',
      }));
    }
  }

  const confirmDelete = async () => {
    setIsSubmitting(true);

    api.delete(`/admin/deleteElectricityEmission/${electricityToDelete.id}`)
      .then(() => {
        dispatch(showNotification({
          message: 'Electricity Emission deleted successfully',
          type: 'success',
        }));
        getAllElectricityEmissions();
      })
      .catch(() => {
        dispatch(showNotification({
          message: 'Failed to delete Electricity Emission',
          type: 'error',
        }));
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setIsSubmitting(false);
      });
  }

  const handleActivateDeactivate = async (emission) => {
    try {
      setIsSubmitting(true);
      const response = await api.post(`/admin/activateDeactivateElectricityEmission/${emission.id}`, {
        is_active: !emission.is_active
      });
      if (response.data.status == 200) {
        dispatch(showNotification({
          message: emission.is_active ? 'Electricity Emission factor deactivated successfully' : 'Electricity Emission factor activated successfully',
          type: "success"
        }));
        getAllElectricityEmissions();
      } else {
        dispatch(showNotification({
          message: emission.is_active ? 'Failed to deactivate Electricity Emission factor' : 'Failed to activate Electricity Emission factor',
          type: "error"
        }));
      }
    } catch (error) {
      console.error('Error activating/deactivating Electricity Emission factor', error);
      dispatch(showNotification({
        message: 'An error occurred',
        type: "error"
      }));
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleRemove = (emission) => {
    setElectricityToDelete(emission);
    setDeleteDialogOpen(true);
  };

  // Handle search with debounce
   const debouncedSearch = React.useCallback(
     debounce((value) => {
       setFilters(prev => ({ ...prev, search: value }));
       setPage(0); // Reset to first page when searching
     }, 500),
     []
   );

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle sorting
  const handleSort = (field) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'ASC' ? 'DESC' : 'ASC'
    }));
  };

  // Apply filters
  const applyFilters = () => {
    setPage(0); // Reset to first page when filters change
    getAllElectricityEmissions(0, rowsPerPage, filters);
    setFilterDialogOpen(false);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      search: '',
      status: '',
      sortBy: 'created_date',
      sortOrder: 'ASC',
      country: '',
      year: '',
      unit: '',
      consumptionType: ''
    });
    setPage(0);
    setFilterDialogOpen(false);
  };

  // Handle filter dialog open
  const handleOpenFilterDialog = () => {
    // Ensure filter options are loaded when dialog opens
    if (filterOptions.countries.length === 0) {
      getFilterOptions();
    }
    setFilterDialogOpen(true);
  };

  useEffect(() => {
    getAllElectricityEmissions();
    getFilterOptions();
  }, [page, rowsPerPage, filters.sortBy, filters.sortOrder, filters.search, 
    filters.status, filters.country, filters.year, filters.unit, filters.consumptionType]);

  const getConsumptionTypeLabel = (type) => {
    if (type === 'USAGE_BASE') return 'Usage-based';
    if (type === 'ESTIMATION') return 'Estimation';
    return type;
  };

  return (
    <>
      <PageHeader title="Electricity Emission Factors">
        <div className="flex justify-end gap-5 items-center mt-5">
          <div className="flex items-center gap-4 flex-1">
            {/* Search Field */}
            <TextField
              size="small"
              placeholder="Search Electricity Emissions..."
              value={searchInput}
              InputProps={{
                startAdornment: <Search className="w-5 h-5 text-gray-500 mr-2" />,
              }}
              onChange={(e) => {
                const value = e.target.value;
                setSearchInput(value)
                debouncedSearch(value); // Debounce the actual filter update
              }}
              className="min-w-[300px]"
            />
            {/* Filter Button */}
            <CustomButton
              className="bg-blue-600 hover:bg-blue-700 text-white"
              text={<><Filter size={18} /> Filters</>}
              click={handleOpenFilterDialog}
            />

            {/* Sort Dropdown */}
            <FormControl size="small" className="min-w-[200px]">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={`${filters.sortBy}-${filters.sortOrder}`}
                label="Sort By"
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setFilters(prev => ({
                    ...prev,
                    sortBy: field,
                    sortOrder: order
                  }));
                }}
              >
                <MenuItem value="created_date-ASC">Date (Oldest First)</MenuItem>
                <MenuItem value="created_date-DESC">Date (Newest First)</MenuItem>
                <MenuItem value="country_name-ASC">Country (A-Z)</MenuItem>
                <MenuItem value="country_name-DESC">Country (Z-A)</MenuItem>
                <MenuItem value="emission_kgco2e-ASC">Emission (Low to High)</MenuItem>
                <MenuItem value="emission_kgco2e-DESC">Emission (High to Low)</MenuItem>
                <MenuItem value="valid_year-ASC">Year (Oldest First)</MenuItem>
                <MenuItem value="valid_year-DESC">Year (Newest First)</MenuItem>
              </Select>
            </FormControl>

          </div>
          <CustomButton
            className="bg-green-600 hover:bg-green-700 text-white"
            text={<><PlusIcon /> Add Electricity Emission</>}
            click={() => setIsAddUpdateElectricityEmissionModalOpen(true)}
          />
        </div>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Country</TableCell>
                <TableCell>Year</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Consumption Type</TableCell>
                <TableCell>Emission KgCo2e</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {electricityEmissions?.map((emission) => (
                <TableRow key={emission.id}>
                  <TableCell>{emission.country_name}</TableCell>
                  <TableCell>{emission.valid_year}</TableCell>
                  <TableCell>{emission.unit_name}</TableCell>
                  <TableCell>{getConsumptionTypeLabel(emission.consumption_type)}</TableCell>
                  <TableCell>{emission.emission_kgco2e} <span className='font-bold'>kgco2e</span></TableCell>
                  <TableCell>
                    <Switch
                      checked={emission.is_active}
                      color='success'
                      onChange={(e) => handleActivateDeactivate(emission)}
                      disabled={isSubmitting}
                    />
                  </TableCell>
                  <TableCell>
                    {emission.created_date ? format(new Date(emission.created_date), "dd-MM-yyyy") : ""}
                  </TableCell>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(emission)}
                      sx={{
                        backgroundColor: '#16A34A',
                        color: 'white',
                        '&:hover': { backgroundColor: '#00bf46' },
                        p: '8px',
                      }}
                    >
                      <PenBoxIcon size={20} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(emission)}
                      sx={{
                        backgroundColor: '#df3e3e',
                        color: 'white',
                        '&:hover': { backgroundColor: '#ff0000' },
                        p: '8px',
                      }}
                    >
                      <DeleteIcon size={20} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  colSpan={8}
                  count={totalElectricityEmissions}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelDisplayedRows={({ from, to, count }) =>
                    `Page ${page + 1} of ${Math.ceil(count / rowsPerPage)} | ${from}-${to} of ${count}`
                  }
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        {/* ADD EDIT DIALOG */}
        <Dialog
          open={isAddUpdateElectricityEmissionModalOpen}
          onClose={() => { setIsAddUpdateElectricityEmissionModalOpen(false); setSelectedElectricityEmission(null) }}
          maxWidth="sm"
          fullWidth
        >
          <div className="flex justify-between items-start pt-2 px-2">
            <DialogTitle className="text-lg font-bold mt-1">
              {selectedElectricityEmission ? "Edit Electricity Emission" : "Add Electricity Emission"}
            </DialogTitle>
            <X
              className="w-6 h-6 cursor-pointer text-gray-500 hover:text-gray-700 transition"
              onClick={() => { setIsAddUpdateElectricityEmissionModalOpen(false); setSelectedElectricityEmission(null) }}
            />
          </div>
          <DialogContent >
            <AddUpdateElectricityEmissionModal
              refetch={getAllElectricityEmissions}
              onClose={() => {
                setIsAddUpdateElectricityEmissionModalOpen(false);
                setSelectedElectricityEmission(null);
              }}
              countryId={selectedElectricityEmission?.country_id}
              initialData={selectedElectricityEmission}
            />
          </DialogContent>
        </Dialog>

        {/* DELETE DIALOG */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the electricity emission factor for {electricityToDelete?.country_name} year {electricityToDelete?.valid_year}? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <div className="flex justify-end gap-5 items-center mt-5">
              <CustomButton
                className="bg-gray-500 hover:bg-gray-700 text-white"
                text="Cancel"
                disabled={isSubmitting}
                click={() => setDeleteDialogOpen(false)}
              />
              <CustomButton
                className="bg-red-600 hover:bg-red-700 text-white"
                click={confirmDelete}
                text='Delete'
                disabled={isSubmitting}
              />
            </div>
          </DialogActions>
        </Dialog>

        {/* Filters Dialog */}
        <Dialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <div className="flex justify-between items-start pt-2 px-2">
            <DialogTitle className="text-lg font-bold mt-1">
              Filter Electricity Emissions
            </DialogTitle>
            <X
              className="w-6 h-6 cursor-pointer text-gray-500 hover:text-gray-700 transition"
              onClick={() => setFilterDialogOpen(false)}
            />
          </div>
          <DialogContent>
            {filterOptions.countries?.length === 0 && (
              <div className="text-center py-2">Loading filter options...</div>
            )}
            <div className="space-y-4 py-2">
              {/* Country Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Country</InputLabel>
                <Select
                  value={filters.country}
                  label="Country"
                  onChange={(e) => setFilters(prev => ({ ...prev, country: e.target.value }))}
                >
                  <MenuItem value="">All Countries</MenuItem>
                  {filterOptions.countries?.map(country => (
                    <MenuItem key={country} value={country}>{country}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Year Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select
                  value={filters.year}
                  label="Year"
                  onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
                >
                  <MenuItem value="">All Years</MenuItem>
                  {filterOptions.years.map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Unit Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Unit</InputLabel>
                <Select
                  value={filters.unit}
                  label="Unit"
                  onChange={(e) => setFilters(prev => ({ ...prev, unit: e.target.value }))}
                >
                  <MenuItem value="">All Units</MenuItem>
                  {filterOptions.units.map(unit => (
                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Consumption Type Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Consumption Type</InputLabel>
                <Select
                  value={filters.consumptionType}
                  label="Consumption Type"
                  onChange={(e) => setFilters(prev => ({ ...prev, consumptionType: e.target.value }))}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {filterOptions.consumptionTypes.map(type => (
                    <MenuItem key={type} value={type}>{getConsumptionTypeLabel(type)}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Status Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </div>
          </DialogContent>
          <DialogActions>
            <div className="flex justify-end gap-5 items-center mt-5 p-2">
              <CustomButton
                className="bg-gray-500 hover:bg-gray-700 text-white"
                text="Reset"
                click={resetFilters}
              />
              <CustomButton
                className="bg-blue-600 hover:bg-blue-700 text-white"
                text="Apply Filters"
                click={applyFilters}
              />
            </div>
          </DialogActions>
        </Dialog>
      </PageHeader>
    </>
  )
}

export default EmissionElectricityMaster